import { createOwnLedgerAnalyticsController, restoreAnalyticsLocalViewSelection } from "@shared/ui/analytics";
import { loadOwnLedgerTransactionSnapshot } from "@shared/ui/ownLedgerClient";
import { isOwnLedgerEnabled } from "@shared/ui/ownLedgerFeatures";
import { TaxController } from "@shared/ui/tax";
import { ImportController } from "@shared/ui/agentic";
import { AgentsController } from "@shared/ui/agents";
import { MarketRiskController } from "@shared/ui/marketRisk";
import { FinancialAstrologyController } from "@shared/ui/financialAstrology";
import { PfolioController } from "@shared/ui/pfolio";
import { renderNativeHistoryPanel, invalidateHistoryPanelCache } from "./nativeHistoryPanel";
import { renderNativeOptionsPanel } from "./nativeOptionsPanel";
import { renderNativeStocksPanel, refreshNativeStocksPanelInBackground } from "./nativeStocksPanel";
import { renderScreenerPanel, type ScreenerSide } from "./screenerView";
import { refreshNativeCallsPanelInBackground } from "./nativeCallsScreener";
import { refreshNativePutsPanelInBackground, teardownPutsMultiSelectPortals } from "./nativePutsScreener";
import { observeStickyTableHeads } from "@shared/ui/stickyTableHead";
import { observePinnedColumnSticky } from "@shared/ui/pinnedColumns";
import { invalidateWheelSnapshots } from "@shared/ui/watchlistData";
import type { WebTabId } from "./tabBar";
import { ensureSignedIn } from "./authUi";
import { IMPORT_TAB_ID, isAgenticFeatureEnabled, isGatedTabId } from "@shared/ui/agenticFeatures";

export type { WebTabId };

type ControllerTabId =
  | "analytics-local"
  | "tax"
  | "market"
  | "astrology"
  | "pfolio"
  | "import"
  | "agents";

const CONTROLLER_PANEL_CLASSES: Record<ControllerTabId, string[]> = {
  "analytics-local": ["sfe-custom-panel", "sfe-analytics-plus-panel"],
  tax: ["sfe-custom-panel", "sfe-tax-panel"],
  market: ["sfe-custom-panel", "sfe-market-panel"],
  astrology: ["sfe-custom-panel", "sfe-astrology-panel"],
  pfolio: ["sfe-custom-panel", "sfe-pfolio-panel"],
  import: ["sfe-custom-panel", "sfe-import-panel-host"],
  agents: ["sfe-custom-panel", "sfe-agents-panel-host"],
};

const CONTROLLER_SIGNIN_META: Record<ControllerTabId, { title: string; view: string }> = {
  "analytics-local": { title: "Analytics", view: "analytics-local" },
  tax: { title: "Tax planning", view: "tax" },
  market: { title: "Market", view: "market" },
  astrology: { title: "Astro", view: "astrology" },
  pfolio: { title: "Pfolio", view: "pfolio" },
  import: { title: "Import", view: "import" },
  agents: { title: "Agentic", view: "agents" },
};

function applyControllerPanelClasses(panel: HTMLElement, id: ControllerTabId): void {
  for (const cls of [
    "sfe-custom-panel",
    "sfe-analytics-plus-panel",
    "sfe-tax-panel",
    "sfe-market-panel",
    "sfe-astrology-panel",
    "sfe-pfolio-panel",
    "sfe-import-panel-host",
    "sfe-agents-panel-host",
  ]) {
    panel.classList.remove(cls);
  }
  for (const cls of CONTROLLER_PANEL_CLASSES[id]) {
    panel.classList.add(cls);
  }
}

type ControllerLike = {
  mount: (panel: HTMLElement) => void;
  refresh?: (force?: boolean) => void | Promise<void>;
};

const controllers: Record<ControllerTabId, ControllerLike> = {
  "analytics-local": createOwnLedgerAnalyticsController((force) =>
    loadOwnLedgerTransactionSnapshot(force === true),
  ),
  tax: new TaxController(),
  market: new MarketRiskController(),
  astrology: new FinancialAstrologyController(),
  pfolio: new PfolioController(),
  import: new ImportController(),
  agents: new AgentsController(),
};

const mounted = new Set<WebTabId>();
const stickyHeadObservers = new WeakMap<HTMLElement, () => void>();
const pinnedColumnObservers = new WeakMap<HTMLElement, () => void>();

function enableStickyTableHeads(panel: HTMLElement): void {
  stickyHeadObservers.get(panel)?.();
  stickyHeadObservers.set(panel, observeStickyTableHeads(panel));
  pinnedColumnObservers.get(panel)?.();
  pinnedColumnObservers.set(panel, observePinnedColumnSticky(panel));
}

function disableStickyTableHeads(panel: HTMLElement): void {
  stickyHeadObservers.get(panel)?.();
  stickyHeadObservers.delete(panel);
  pinnedColumnObservers.get(panel)?.();
  pinnedColumnObservers.delete(panel);
}

function isControllerTab(id: WebTabId): id is ControllerTabId {
  return id in controllers;
}

export async function activateWebTab(panel: HTMLElement, id: WebTabId): Promise<void> {
  panel.hidden = false;
  panel.classList.add("sfe-web-panel-active");
  panel.setAttribute("data-wheel-view", id);

  if (id === "puts" || id === "calls") {
    await renderScreenerPanel(panel, id as ScreenerSide, { force: false });
    mounted.add(id);
    enableStickyTableHeads(panel);
    return;
  }

  if (id === "options") {
    await renderNativeOptionsPanel(panel, { force: false });
    mounted.add(id);
    enableStickyTableHeads(panel);
    return;
  }

  if (id === "stocks") {
    await renderNativeStocksPanel(panel, { force: false });
    mounted.add(id);
    enableStickyTableHeads(panel);
    return;
  }

  if (id === "history-local") {
    if (!isOwnLedgerEnabled()) return;
    invalidateHistoryPanelCache("own_ledger");
    await renderNativeHistoryPanel(panel, { force: true, ledgerSource: "own_ledger" });
    mounted.add(id);
    enableStickyTableHeads(panel);
    return;
  }

  if (!isControllerTab(id)) return;
  if (isGatedTabId(id) && !isAgenticFeatureEnabled()) return;
  if ((id === "analytics-local" || id === "pfolio") && !isOwnLedgerEnabled()) return;

  const signInMeta = CONTROLLER_SIGNIN_META[id];
  if (!(await ensureSignedIn(panel, signInMeta))) {
    mounted.delete(id);
    return;
  }

  applyControllerPanelClasses(panel, id);
  const controller = controllers[id];
  if (id === "analytics-local") {
    restoreAnalyticsLocalViewSelection();
  }
  if (id === IMPORT_TAB_ID) {
    (controller as ImportController).mount(panel, { returnPath: "/web/" });
  } else {
    controller.mount(panel);
  }
  mounted.add(id);

  if (typeof controller.refresh === "function") {
    await controller.refresh();
  }
  enableStickyTableHeads(panel);
}

export function deactivateWebTab(panel: HTMLElement): void {
  teardownPutsMultiSelectPortals(panel);
  disableStickyTableHeads(panel);
  panel.hidden = true;
  panel.classList.remove("sfe-web-panel-active");
  panel.removeAttribute("data-wheel-view");
}

export async function refreshActiveDataTab(panel: HTMLElement, id: WebTabId): Promise<void> {
  if (id === "puts") {
    await refreshNativePutsPanelInBackground(panel);
    return;
  }
  if (id === "calls") {
    await refreshNativeCallsPanelInBackground(panel);
    return;
  }
  if (id === "options") {
    invalidateWheelSnapshots();
    await renderNativeOptionsPanel(panel, { force: true });
    return;
  }
  if (id === "stocks") {
    await refreshNativeStocksPanelInBackground(panel);
    return;
  }
  if (id === "history-local") {
    if (!isOwnLedgerEnabled()) return;
    invalidateHistoryPanelCache("own_ledger");
    await renderNativeHistoryPanel(panel, { force: true, ledgerSource: "own_ledger" });
    return;
  }
  if (isControllerTab(id)) {
    if (isGatedTabId(id) && !isAgenticFeatureEnabled()) return;
    if ((id === "analytics-local" || id === "pfolio") && !isOwnLedgerEnabled()) return;
    const signInMeta = CONTROLLER_SIGNIN_META[id];
    if (!(await ensureSignedIn(panel, signInMeta))) {
      mounted.delete(id);
      return;
    }
    applyControllerPanelClasses(panel, id);
    const controller = controllers[id];
    // Analytics: patch in place — do not remount the shell.
    if (
      id === "analytics-local" &&
      mounted.has(id) &&
      panel.querySelector(".sfe-analytics-plus-body")
    ) {
      if (typeof controller.refresh === "function") {
        await controller.refresh(true);
      }
      return;
    }
    if (id === IMPORT_TAB_ID) {
      (controller as ImportController).mount(panel, { returnPath: "/web/" });
    } else {
      controller.mount(panel);
    }
    if (typeof controller.refresh === "function") {
      await controller.refresh(true);
    }
  }
}
